
import React, { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";

interface AutoRenewalToggleProps {
  isEnabled: boolean;
  subscriptionId: string;
  onToggle: (id: string, currentValue: boolean) => Promise<void>;
}

export function AutoRenewalToggle({ isEnabled, subscriptionId, onToggle }: AutoRenewalToggleProps) {
  const [isUpdating, setIsUpdating] = useState(false);
  const [autoRenewal, setAutoRenewal] = useState(isEnabled);
  const { toast } = useToast();

  // Update local state when props change (important for synchronization)
  useEffect(() => {
    setAutoRenewal(isEnabled);
  }, [isEnabled]);

  const handleAutoRenewalToggle = async () => {
    try {
      setIsUpdating(true);
      // Pass the current value of autoRenewal (not the future value)
      await onToggle(subscriptionId, autoRenewal);
      // Only update state after successful database update
      setAutoRenewal(!autoRenewal);
      
      // Show feedback to the user
      toast({
        title: "Auto-renewal updated",
        description: autoRenewal 
          ? "Auto-renewal has been disabled" 
          : "Auto-renewal has been enabled",
      });
    } catch (error) {
      console.error('Failed to toggle auto-renewal:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update auto-renewal setting",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-gray-600">Auto-renewal</span>
      <Switch 
        checked={autoRenewal}
        onCheckedChange={handleAutoRenewalToggle}
        disabled={isUpdating}
      />
    </div>
  );
}
